// src/pages/admin/AdminPanel.jsx
// Panel de gestión en /gestion. Tabs: Resumen / Usuarios / Sistema.
// Lee /users de Firestore (admin pasa rules con isAdmin()).
//
// NOTE (UX audit ISSUE 11): tour de onboarding intencionalmente omitido en
// el panel admin. El tour solo se muestra en ClientDashboard para clientes
// pagos — en /gestion el operador es un usuario interno técnico que no
// necesita el walkthrough.

import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { collection, onSnapshot, getFirestore } from 'firebase/firestore';
import app from '../../firebase';
import { useAuth } from '../../auth/AuthContext';
import { useLogoutFlow } from '../../auth/useLogoutFlow';
import SplashScreen from '../../components/SplashScreen';
import MetricsCards from '../../components/admin/MetricsCards';
import UserList from '../../components/admin/UserList';
import SystemHealth from '../../components/admin/SystemHealth';
import '../../styles/admin.css';

const fs = getFirestore(app);

export default function AdminPanel() {
  const { user, userData } = useAuth();
  const { showLogoutSplash, startLogout, handleLogoutComplete } = useLogoutFlow();
  const [tab, setTab] = useState('resumen');
  const [users, setUsers] = useState([]);

  // FASE UX: useLogoutFlow encapsula clear flag + signOut + splash.
  const handleSignOut = startLogout;
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);

  useEffect(() => {
    const col = collection(fs, 'users');
    const unsub = onSnapshot(col,
      (snap) => {
        const list = snap.docs.map((d) => ({ uid: d.id, ...d.data() }));
        setUsers(list);
        setUsersLoading(false);
      },
      (err) => {
        console.error('[admin] users onSnapshot error:', err);
        setUsersError(err);
        setUsersLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const meEmail = userData?.email || user?.email || '—';

  // FIX BUG 2: early return cuando hay logout splash activo.
  if (showLogoutSplash) {
    return <SplashScreen type="logout" onComplete={handleLogoutComplete} />;
  }

  return (
    <div className="admin-shell">
      <Helmet>
        <title>Gestión · Edge Elite</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="admin-header">
        <div className="admin-header-title">
          <span className="accent-dot">●</span>
          Panel de Gestión · Edge Elite
        </div>
        <div className="admin-header-actions">
          <span className="me-email">{meEmail}</span>
          <button className="admin-logout-btn" onClick={handleSignOut} type="button">Cerrar sesión</button>
        </div>
      </header>

      <nav className="admin-tabs" role="tablist">
        <button className="admin-tab" data-active={tab === 'resumen'} onClick={() => setTab('resumen')} type="button">Resumen</button>
        <button className="admin-tab" data-active={tab === 'usuarios'} onClick={() => setTab('usuarios')} type="button">Usuarios</button>
        <button className="admin-tab" data-active={tab === 'sistema'} onClick={() => setTab('sistema')} type="button">Sistema</button>
      </nav>

      <main className="admin-content">
        {usersError && (
          <div className="admin-section" style={{ background: 'var(--a-neg-soft)', padding: 12, borderRadius: 8, color: 'var(--a-neg)' }}>
            Error leyendo usuarios: {String(usersError.message || usersError.code || usersError)}.
            Verificá que el doc /users/{user?.uid?.slice(0,8)}… tiene <code>role: 'admin'</code>.
          </div>
        )}
        {usersLoading ? (
          <div className="admin-section" style={{ color: 'var(--a-text-2)' }}>Cargando usuarios…</div>
        ) : (
          <>
            {tab === 'resumen' && (
              <>
                <MetricsCards users={users} />
                <SystemHealth />
              </>
            )}
            {tab === 'usuarios' && <UserList users={users} />}
            {tab === 'sistema' && <SystemHealth />}
          </>
        )}
      </main>
    </div>
  );
}
